import { getScrollableParent } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { Heading, Box, Stack, Text, Alert } from 'braid-design-system';
import React from 'react';

import { useGetProfileVisibility } from 'src/components/Profile/ProfileVisibilityV2/data';
import { RESUME } from 'src/components/Router';
import { Sync } from 'src/components/Sync/Sync';
import { isSmallDevice } from 'src/modules/device';
import { useFocus } from 'src/modules/hooks/useFocus';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import { SYNCING, stateForAction } from 'src/modules/syncing';

import { useResumes } from '../../data';
import { Actions } from '../../reducer';

import translations from './.vocab';
import { PrivacyInfo } from './PrivacyInfo/PrivacyInfo';
import { ResumeItemList } from './ResumeItemList/ResumeItemList';

const scrollAction = (ref: any) => {
  if (isSmallDevice()) {
    const parent = getScrollableParent(ref);
    if (parent) {
      parent.scrollTop = ref.offsetTop;
    }
  }
};

export const ManageResumes = React.memo(() => {
  const { t } = useTranslations(translations);
  const { resumes, defaultResumeId } = useResumes();

  const syncingState = useTypedSelector(
    ({ syncing }) => {
      const { state: stateUpdateDefault } = stateForAction({
        state: { syncing },
        parent: RESUME,
        action: Actions.RESUME_UPDATE_DEFAULT,
      });

      const { state: stateDelete } = stateForAction({
        state: { syncing },
        parent: RESUME,
        action: Actions.RESUME_DELETE,
      });

      return stateUpdateDefault !== 'SYNCED' ? stateUpdateDefault : stateDelete;
    },
    (prevState, nextState) =>
      JSON.stringify(prevState) === JSON.stringify(nextState),
  );

  const hasResumes = resumes.length > 0;
  const noDefaultResume =
    syncingState !== SYNCING && hasResumes && defaultResumeId === '';

  const { profileVisibilityLevel } = useGetProfileVisibility();

  const manageResumesRef = useFocus({
    card: 'resume',
    fieldName: 'manage-resumes',
    scrollOptions: { desktopOffset: 0, mobileOffset: 0 },
    action: scrollAction,
    domReady: hasResumes,
  });

  return hasResumes ? (
    <Stack space="xlarge">
      <PrivacyInfo />
      <Stack space="medium">
        <Box
          display="flex"
          justifyContent="spaceBetween"
          alignItems="center"
          position="relative"
          ref={manageResumesRef}
          data-automation="manage-resumes"
        >
          <Heading level="3" component="h2">
            {t('Manage resumés')}
          </Heading>
          <Sync state={syncingState} />
        </Box>
        {noDefaultResume ? (
          <Alert tone="critical">
            <Text>
              {profileVisibilityLevel === 'Standard'
                ? t(
                    'Select a default resumé to maintain your resumé visibility to employers.',
                  )
                : t('Select a default resumé.')}
            </Text>
          </Alert>
        ) : null}
        <ResumeItemList />
      </Stack>
    </Stack>
  ) : (
    <PrivacyInfo />
  );
});
